import { ExclamationCircleIcon } from '@heroicons/react/outline'
import { UseFormReturnExt } from '@types'
import clsx from 'clsx'
import ResetButton from 'components/forms/ResetButton'
import { OnSubmitResult } from 'hooks/useBaseForm'
import {
  isDiff,
  isNonEmptyString,
  isPresent,
  isSame,
} from 'lib/utils'
import {
  JSXElementConstructor,
  KeyboardEvent,
  PropsWithoutRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import {
  FieldValues,
  useFormContext,
  useWatch,
} from 'react-hook-form'

export interface Props
  extends PropsWithoutRef<
    JSX.IntrinsicElements['input'] & JSX.IntrinsicElements['textarea']
  > {
  onEnterDown?: (...args: any) => Promise<void | OnSubmitResult>
  name: string
  className?: string
  textArea?: boolean
  withResetButton?: boolean
  resetButtonClasses?: string
  inputClassName?: string
  type?: 'text' | 'password' | 'email' | 'number' | 'tel' | 'date'
  wrapperClasses?: string
  initFocus?: boolean
  absoluteError?: boolean
  isLoading?: boolean
  disabled?: boolean
  extError?: string
}

let TextInput = (props: Props) => {
  let {
    onEnterDown,
    name,
    textArea = false,
    type = 'text',
    className = 'form-input',
    initFocus,
    absoluteError = false,
    wrapperClasses = 'relative',
    resetButtonClasses,
    inputClassName,
    withResetButton = true,
    disabled = false,
    isLoading = false,
    extError,
    ...rest
  } = props

  let InputElement = (textArea
    ? 'textarea'
    : 'input') as unknown as JSXElementConstructor<any>

  let {
    setValue,
    clearErrors,
    register,
    setFocus,
    formState: {
      isSubmitting,
      errors: { [name]: inputErrors },
    },
    handleSubmit,
    defaultValues,
  } = useFormContext() as UseFormReturnExt<FieldValues>

  useEffect(() => {
    initFocus && setFocus(name)
  }, [initFocus, name, setFocus])

  let value = useWatch({ name })
  let { ref, ...registerRest } = register(name)

  let inputRef = useRef<
    HTMLInputElement | HTMLTextAreaElement | null
  >(null)
  let handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (isSame(e.key, 'Escape')) {
        e.preventDefault()
        inputRef.current!.blur()
      }

      if (isSame(e.key, 'Enter')) {
        e.preventDefault()
        inputRef.current!.blur()
        isPresent(onEnterDown) && void handleSubmit(onEnterDown)()
      }
    },
    [handleSubmit, onEnterDown]
  )

  let error = useMemo(
    () =>
      Array.isArray(inputErrors)
        ? inputErrors[0]
        : inputErrors?.message || inputErrors,
    [inputErrors]
  )

  let rootClassName = clsx(className, {
    'form-error': isNonEmptyString(error),
    'form-disabled': isSubmitting || isLoading,
  })

  let resetValue = useCallback(() => {
    setValue(name, defaultValues[name])
    clearErrors(name)
  }, [clearErrors, defaultValues, name, setValue])

  return (
    <>
      <div className={clsx(wrapperClasses)}>
        <InputElement
          id={name}
          className={clsx(
            'rounded-md w-full',
            rootClassName,
            inputClassName
          )}
          readOnly={isSubmitting || isLoading}
          type={type}
          onKeyDown={handleKeyDown}
          ref={(e: HTMLInputElement | HTMLTextAreaElement | null) => {
            ref(e)
            inputRef.current = e
          }}
          disabled={disabled}
          {...registerRest}
          {...rest}
        />

        {withResetButton && !disabled && (
          <ResetButton
            condition={
              !isSubmitting &&
              !isLoading &&
              isNonEmptyString(value) &&
              isDiff(defaultValues[name], value)
            }
            onClick={resetValue}
            className={resetButtonClasses}
          />
        )}
      </div>

      {(isNonEmptyString(error) || isNonEmptyString(extError)) && (
        <div
          className={clsx('mb-3.5 flex items-start lg:mt-1 mt-1.5', {
            absolute: absoluteError,
          })}
        >
          <ExclamationCircleIcon className='flex-shrink-0 w-4 h-4 text-warning' />
          <div className='ml-1 text-xs leading-tight text-warning'>
            {error ?? extError}
          </div>
        </div>
      )}
    </>
  )
}

export default TextInput
